import { DocumentNode, gql, useMutation } from '@apollo/client';
import { MenuItem, useDisclosure, useToast } from '@chakra-ui/react';
import * as React from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';
import { logError } from '@/imports/logging/ClientLogger';
import { Trash2Icon } from '@/imports/ui/chakra/feather';
import { content_deleted } from '@/analytics';
import { minutesSince } from '@/analytics/contentAnalysis';

import { DeleteCommentDropdownItem_CommentFragment } from './graphql-types/DeleteCommentDropdownItem_CommentFragment';
import {
  DeleteCommentDropdownItem_deleteComment,
  DeleteCommentDropdownItem_deleteCommentVariables,
} from './graphql-types/DeleteCommentDropdownItem_deleteComment';

const DELETE_COMMENT = gql`
  mutation DeleteCommentDropdownItem_deleteComment($commentId: String!) {
    deleteComment(input: { id: $commentId }) {
      data {
        _id
        postV2 {
          _id
          comments {
            _id
            comments {
              _id
            }
          }
        }
      }
    }
  }
`;

export const DeleteCommentDropdownItem: React.FC<{
  comment: DeleteCommentDropdownItem_CommentFragment;
}> & {
  commentFragment: DocumentNode;
} = ({ comment }) => {
  const confirmDialog = useDisclosure();
  const toast = useToast();
  const [deleteComment] = useMutation<
    DeleteCommentDropdownItem_deleteComment,
    DeleteCommentDropdownItem_deleteCommentVariables
  >(DELETE_COMMENT);

  const deleteConfirm = async (): Promise<void> => {
    try {
      await deleteComment({
        variables: {
          commentId: comment._id,
        },
      });

      // Track content deletion
      content_deleted({
        entity: {
          id: comment._id,
          type: 'comment',
        },
        content: {
          contentType: 'comment',
        },
        timing: {
          timeSinceCreation: (comment as any).dateCreated ? minutesSince((comment as any).dateCreated) : 0,
        },
      });

      toast({ title: 'Successfully deleted the comment.', status: 'success' });
    } catch (err) {
      toast({
        title: 'Failed to delete comment.',
        status: 'error',
      });
      logError(err);
    }
  };

  return (
    <>
      <MenuItem icon={<Trash2Icon />} onClick={confirmDialog.onOpen}>
        Delete
      </MenuItem>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title="Delete this Comment"
        body="Are you sure you want to permanently remove this comment?"
        confirmButtonText="Delete Comment"
        onConfirm={deleteConfirm}
      />
    </>
  );
};

DeleteCommentDropdownItem.commentFragment = gql`
  fragment DeleteCommentDropdownItem_CommentFragment on Comment {
    _id
    dateCreated
  }
`;
